import type * as GeoJSON from 'geojson';
import { BaseQueryFn } from '@reduxjs/toolkit/query';
import { AxiosError, AxiosRequestConfig } from 'axios';
import { createApi } from '@reduxjs/toolkit/query/react';
import { SmartSelection } from '@larki/mp/lib/shape';
import { MapVersion } from '@larki/shared/type';

import { larkiApi } from '../../utilities/api';
import { processJobList } from '../actions';

import { Quote } from '../../mp/services/coverage/workers/quote.worker';
import { redirectTo } from '../../hooks/useRedirect';
import routes from '../../routes/constants';
import { _addMetadata, METADATA_PREFIX } from '../../utilities/bookmark';
import objectUtilities from '../../utilities/object';

export type LarkiErrorResponse = {
  status?: number;
  data: any;
  originalError?: AxiosError;
};

export class LarkiUnauthorizedAccessToJobError extends Error {
  originalError?: AxiosError;
  constructor(originalError?: AxiosError) {
    super('You are not authorized to access this job');
    this.originalError = originalError;
  }
}

type LarkiBaseQueryFnArgs = {
  url: string;
  method?: AxiosRequestConfig['method'];
  data?: AxiosRequestConfig['data'];
  params?: AxiosRequestConfig['params'];
  headers?: AxiosRequestConfig['headers'];
};
function larkiApiBaseQuery<T>(): BaseQueryFn<
  LarkiBaseQueryFnArgs,
  T,
  LarkiErrorResponse
> {
  return async ({ url, method, data, params, headers }) => {
    try {
      const result = await larkiApi({
        url,
        method,
        data,
        params,
        headers,
      });
      return { data: result.data };
    } catch (err) {
      if (err instanceof AxiosError) {
        if (err.response?.status === 401) {
          redirectTo(routes.login);
        }
        return {
          error: {
            status: err.response?.status,
            data: err.response?.data || err.message,
          },
        };
      }
      return {
        error: {
          data: err,
        },
      };
    }
  };
}

export const RTK_API_REDUCER_PATH = 'rtk';

export type Job = {
  id: number;
  status:
    | 'draft'
    | 'pending'
    | 'paid'
    | 'inprogress'
    | 'completed'
    | 'cancelled'
    | 'demo';
  address: string | null;
  full_address: string | null;
  city: string | null;
  state: string | null;
  postcode: string | null;
  place_geometry: GeoJSON.Point | null;
  project: { id: number; name: string | null };
  quote: Quote | null;
  anon_order_expiry: Date | null;
};

export type Project = {
  id: number;
  name: string | null;
};

export type Bookmark = {
  share_token: string;
  metadata?: Record<string, string>;
  redirect_url: string;
};

export type UserProject = {
  user_id: number;
  project_id: number;
  invite_status: 'pending' | 'accepted' | 'rejected' | 'requested';
};

type BaseQueryFnCallable<Args extends LarkiBaseQueryFnArgs, Result, Error> = (
  arg: Parameters<BaseQueryFn<Args, Result, Error>>[0]
) => ReturnType<BaseQueryFn<Args, Result, Error>>;
type LarkiQueryFn<T = unknown> = BaseQueryFnCallable<
  LarkiBaseQueryFnArgs,
  T,
  LarkiErrorResponse
>;

export type CompanyProfile = {
  id: number;
  name: string;
  specialization: string;
  address: string;
  phone: string;
  website: string;
  business_number: string;
  logo_url: string;
};

type User = {
  id: number;
  full_name: string;
  email: string;
  phone: string;
};

export const rtkApi = createApi({
  reducerPath: RTK_API_REDUCER_PATH,
  baseQuery: larkiApiBaseQuery(),
  tagTypes: ['Project', 'User', 'CompanyProfile'],
  endpoints: (build) => ({
    /**
     * Don't use directly, use `useUser` instead.
     */
    getUserProfile: build.query<
      {
        id: number;
        full_name: string;
        profile_image?: string;
        has_used_trial: boolean;
        role: string;
        customer: {
          memberships: { status: string; entitlements: string[] }[];
        };
        profession?: string;
      },
      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
      void
    >({
      query: () => ({ url: 'user/profile' }),
      providesTags: ['User'],
      // TODO: use user ID to query to invalidate cache on change user
    }),

    /**
     * Get projects associated with the current user.
     *
     * `getUserProjects` should have to wait until the user has been fetched
     *  so the `isAdmin` value is up to date.
     *
     * Usage:
     * ```
     * const currentUser = ();
     * const {...} = useGetUserProjectsQuery({skip: !currentUser.isSuccess})
     * ```
     */
    getUserProjects: build.query<
      {
        jobToProjectId: Record<number, number>;
        projects: Record<
          number,
          {
            id: number;
            mp_enabled: boolean;
            created_at: string;
            project: { id: number; name?: string | null };
            jobs: { id: number; lga: boolean }[];
          }
        >;
      },
      { isAdmin: boolean; includeDemo?: boolean }
    >({
      query: ({ isAdmin, includeDemo = false }) => ({
        url: 'project',
        params: {
          isAdmin,
          includeDemo,
          inviteStatus: ['accepted'],
        },
      }),
      providesTags: ['Project'],
      transformResponse: (data: any) => {
        return {
          jobToProjectId: data.projects?.reduce((acc: any, project: any) => {
            if (project.jobs.length === 0) {
              return acc;
            }
            for (const job of project.jobs) {
              acc[job.id] = project.id;
            }
            return acc;
          }, {}),
          projects: processJobList(data),
          counts: data.statusCounts,
        };
      },
    }),

    /**
     * Create an new job.
     */
    createJob: build.mutation<
      { id: number },
      {
        address?: string | null;
        city?: string | null;
        state?: string | null;
        postcode?: string | null;
        country?: string | null;
        message?: string | null;
        customer_software?: string | null;
        point_cloud_format?: string | null;
        projectName: string | null;
        mergeProjectId?: number;
        place_geometry?: GeoJSON.Point | null;
        anon_order_expiry?: Date | null;
      }
    >({
      query: (data) => ({
        url: 'job',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Project'],
    }),

    // TODO: add appropriate Response & Request type
    updateJob: build.mutation({
      query: ({ id, data }) => ({
        url: `/job/${id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['Project'],
    }),

    /**
     * Get a job by ID.
     */
    getJob: build.query<JobWithSmartSelections, number>({
      queryFn: async (jobId, _, __, baseQuery) => {
        const resp = await (baseQuery as LarkiQueryFn<Job>)({
          url: `/job/${jobId}`,
        });
        if (resp.error) {
          if (resp.error.status === 403) {
            return {
              error: new LarkiUnauthorizedAccessToJobError(
                resp.error.originalError
              ),
            };
          }
          return { error: resp.error };
        }
        const job = resp.data;
        const [smartSelections, property] = await Promise.all([
          new Promise<SmartSelection[]>((resolve, reject) => {
            if (!job.place_geometry) return resolve([]);
            return Promise.resolve(
              baseQuery({
                url: `/engine/product/selections`,
                method: 'POST',
                data: {
                  location: {
                    lng: job.place_geometry.coordinates[0],
                    lat: job.place_geometry.coordinates[1],
                  },
                  address: job.full_address,
                  city: job.city,
                  state: job.state,
                  postcode: job.postcode,
                },
              })
            )
              .then((resp) => {
                if (resp.error) return reject(resp.error);
                const data = resp.data as {
                  property_vicinity: SmartSelection[];
                  neighbourhood: SmartSelection;
                  buildings: SmartSelection[];
                  roads: SmartSelection[];
                };
                return resolve([
                  ...data.property_vicinity,
                  data.neighbourhood,
                  ...data.buildings,
                  ...data.roads,
                ]);
              })
              .catch(reject);
          }),
          new Promise<Property | null>((resolve, reject) => {
            if (!job.place_geometry) return resolve(null);
            return Promise.resolve(
              baseQuery({
                url: '/engine/selections/property',
                method: 'POST',
                data: {
                  location: {
                    lat: job.place_geometry.coordinates[1],
                    lng: job.place_geometry.coordinates[0],
                  },
                },
              })
            )
              .then((resp) => {
                if (resp.error) return reject(resp.error);
                return resolve(resp.data as Property);
              })
              .catch(reject);
          }),
        ]);
        const ret = { ...job, smartSelections, property };
        return { data: ret };
      },
      providesTags: ['Project'],
    }),

    /**
     * Shape downloads.
     */
    geospatialDownload: build.mutation<
      string,
      {
        jobId: number;
        shapes: GeospatialDownloadShapeInput[];
        fileType: GeospatialDownloadFileType;
      }
    >({
      query: ({ jobId, shapes, fileType }) => ({
        url: `/job/${jobId}/geospatial/download`,
        method: 'POST',
        data: {
          shapes,
          fileType,
        },
      }),
    }),

    /**
     * Get a project by ID.
     */
    getProject: build.query<
      Project,
      {
        projectId: number;
        params?: {
          includeJobs?: boolean;
          includePointCloud?: boolean;
          includeQuote?: boolean;
        };
      }
    >({
      query: ({ projectId, params = {} }) => {
        console.log(projectId, params)
        return {
          url: `/project/${projectId}`,
          params,
        };
      },
      providesTags: ['Project'],
    }),

    /**
     * Check access.
     */
    checkProjectAccess: build.query<UserProject, { projectId: number }>({
      query: ({ projectId }) => ({
        url: `/project/${projectId}/check-access`,
      }),
    }),

    checkJobAccess: build.query<UserProject, { jobId: number }>({
      query: ({ jobId }) => ({
        url: `/job/${jobId}/check-access`,
      }),
    }),

    /**
     * Request access to a page, with an existing share token.
     */
    requestAccess: build.mutation<
      unknown,
      { shareToken?: string; projectId?: number; jobId?: number }
    >({
      queryFn: async ({ shareToken, projectId, jobId }, _, __, baseQuery) => {
        const url = shareToken
          ? `/project/bookmark/${shareToken}/request`
          : projectId
            ? `/project/${projectId}/request-access`
            : jobId
              ? `/job/${jobId}/request-access`
              : null;
        if (!url)
          throw new Error('Needed one of [shareToken, projectId, jobId]');
        const resp = await (baseQuery as LarkiQueryFn)({
          url,
          method: 'POST',
        });
        if (resp.error) {
          if (resp.error.status === 409) {
            return { data: null };
          }
          return { error: resp.error };
        }
        return { data: resp.data };
      },
      // if we request access to a project, we need to invalidate its cache
      //  so that the next time we try and view it, we don't get an unauthorized error
      invalidatesTags: ['Project'],
    }),

    /**
     * Open a bookmark.
     */
    openBookmark: build.query<
      { bookmark: Bookmark; isRejected?: boolean; redirectUrl?: string },
      { shareToken: string; redirectToRequestAccess?: boolean }
    >({
      queryFn: async (
        { shareToken, redirectToRequestAccess },
        _,
        __,
        baseQuery
      ) => {
        const resp = await (baseQuery as LarkiQueryFn<{ bookmark: Bookmark }>)({
          url: `/ln/${shareToken}`,
        });
        if (resp.error) {
          if (resp.error.status === 403) {
            const shareToken = resp.error.data?.bookmark?.share_token;
            if (shareToken && redirectToRequestAccess) {
              return {
                data: {
                  ...resp.error.data,
                  redirectUrl: routes.requestAccess.withShareToken(shareToken),
                },
              };
            }
            if (resp.error.data?.userProject?.invite_status === 'rejected') {
              return {
                data: {
                  ...resp.error.data,
                  isRejected: true,
                },
              };
            }
          }
          return { data: resp.error.data };
        }
        const metadata = resp.data.bookmark.metadata;
        const metadataFlat = metadata
          ? objectUtilities.flatten(metadata, METADATA_PREFIX)
          : null;
        const redirectUrl = _addMetadata(
          resp.data.bookmark.redirect_url,
          metadataFlat
        );
        return { data: { ...resp.data, redirectUrl } };
      },
    }),

    reinitiateJob: build.mutation<unknown, { jobId: number }>({
      query: ({ jobId }) => ({
        url: `/job/${jobId}/order/reinitiate`,
        method: 'POST',
      }),
    }),

    supersedeActivity: build.mutation<
      unknown,
      { projectId: number; activityId: number; isSuperseded: boolean }
    >({
      query: ({ projectId, activityId, isSuperseded }) => ({
        url: `/project/${projectId}/activity/${activityId}/supersede`,
        method: 'PATCH',
        data: { isSuperseded },
      }),
    }),

    getCompanyProfile: build.query<CompanyProfile & { owner: User }, undefined>(
      {
        query: () => ({
          url: '/organisation/profile',
        }),
        providesTags: ['CompanyProfile'],
      }
    ),

    updateCompanyProfile: build.mutation<
      CompanyProfile & { owner: User },
      Omit<CompanyProfile, 'id'>
    >({
      query: (data) => ({
        url: '/organisation/profile',
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['CompanyProfile'],
    }),

    updateCompanyLogo: build.mutation<unknown, { filePath: string }>({
      query: ({ filePath }) => ({
        url: '/organisation/company-logo',
        method: 'PATCH',
        data: { filePath },
      }),
      invalidatesTags: ['CompanyProfile'],
    }),
  }),
});

interface JobWithSmartSelections extends Job {
  smartSelections: SmartSelection[];
  property: Property | null;
}

export type Property = {
  geometry: GeoJSON.Polygon;
};
export type GeospatialDownloadShapeInput = GeoJSON.Feature<
  GeoJSON.Polygon,
  { name?: string | null }
>;

export type GeospatialDownloadFileType =
  | 'geojson'
  | 'shp'
  | 'kml'
  | 'dxf'
  | 'dxf-truncated';

export const {
  useGetUserProfileQuery,
  useGetUserProjectsQuery,
  useCreateJobMutation,
  useUpdateJobMutation,
  useGetJobQuery,
  useGeospatialDownloadMutation,
  useGetProjectQuery,
  useCheckProjectAccessQuery,
  useCheckJobAccessQuery,
  useRequestAccessMutation,
  useLazyOpenBookmarkQuery,
  useOpenBookmarkQuery,
  useReinitiateJobMutation,
  useSupersedeActivityMutation,
  useGetCompanyProfileQuery,
  useUpdateCompanyProfileMutation,
  useUpdateCompanyLogoMutation,
} = rtkApi;
